import { useClientQuery } from "client/accounts/hooks";
import { ClientDetailPatient } from "client/accounts/types";

import {
  ForMyPetDescription,
  ForMyPetInvitation,
  ForMyPetLabel,
  PetSearch,
  speciesAfterPetSelection,
} from "../components/ForMyPetFilter";
import { PRODUCT_FILTER_DEFINITIONS } from "../constants/filterDefinitions";
import {
  defineFilter,
  FilterDefinition,
  FilterValue,
  ProductSearchFilters,
  SearchFilterOption,
  SelectedProductFilters,
} from "../types";

type PetOptions = { patients?: SearchFilterOption<number>[] };

const useShopperPets = (): ClientDetailPatient[] => {
  const { data: client } = useClientQuery();
  return client ? client.patients.filter((patient) => patient.is_active) : [];
};

/**
 * Param `patient_ids`, response key `patients`. An empty option list drops the section like any
 * other empty one.
 */
const useForMyPetFilterDefinition = <
  TSearch extends PetSearch,
  TFilterOptions extends PetOptions,
>(): FilterDefinition<TSearch, FilterValue, TFilterOptions> => {
  const pets = useShopperPets();

  return defineFilter<TSearch, number, TFilterOptions>({
    key: "for_my_pet",
    label: <ForMyPetLabel />,
    description: <ForMyPetDescription />,
    singularLabel: "Pet",
    emptyState: <ForMyPetInvitation />,
    optionsFrom: (options) => options.patients,
    selectedFrom: (filters) => filters.patient_ids ?? [],
    // TSearch is only bounded by these fields, so a literal of them is not provably its Partial.
    toUpdate: (next, current) =>
      ({
        patient_ids: next,
        species: speciesAfterPetSelection(current, pets, next),
      }) as Partial<TSearch>,
  });
};

export const useProductFilterDefinitions = (): FilterDefinition<
  SelectedProductFilters,
  FilterValue,
  ProductSearchFilters
>[] => [
  useForMyPetFilterDefinition<SelectedProductFilters, ProductSearchFilters>(),
  ...PRODUCT_FILTER_DEFINITIONS,
];
